/**
 * App — Top-Level-Router (React Router / BrowserRouter via main.tsx).
 *
 * Routen:
 *   "/"      → öffentliche Landingpage (eager geladen — Inhalt der Startseite).
 *   "/login" → Login (lazy; eingeloggt → /dashboard).
 *   "/*"     → auth-gated AppShell (lazy code-split — die LP zieht so NICHT das
 *              App-Bundle aus Screens + Store).
 *
 * Code-Splitting: AppShell + Login werden via React.lazy in eigene Chunks
 * ausgelagert; nur Landing + Router + Auth-Gate liegen im Haupt-Bundle.
 *
 * OWNERSHIP: Dieser File rendert KEINEN EditModal/EditForm.
 */
import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './lib/auth';
import Landing from './landing/Landing';

// Authentifizierte Bereiche getrennt gebündelt (Code-Splitting).
const AppShell = lazy(() => import('./AppShell'));
const Login = lazy(() => import('./screens/Login'));

// ---- Lade-Spinner (Auth-Gate + Suspense-Fallback) --------------------------

function Spinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-canvas)' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-faint)' }}>
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 22 }}></i>
        <div style={{ marginTop: 12, fontSize: 13 }}>Sitzung wird geladen …</div>
      </div>
    </div>
  );
}

// ---- Top-Level-App mit React-Router-Routen ---------------------------------

export default function App() {
  const auth = useAuth();
  const navigate = useNavigate();

  // Theme auch im Login-/Loading-Zustand korrekt halten.
  useEffect(() => {
    const t = localStorage.getItem('gads_theme') ?? 'light';
    document.documentElement.className = `theme-${t}`;
  }, []);

  return (
    <Routes>
      {/* Öffentliche Landing Page */}
      <Route
        path="/"
        element={<Landing onEnterApp={() => navigate('/dashboard')} />}
      />

      {/* Login-Seite: wenn bereits eingeloggt → /dashboard */}
      <Route
        path="/login"
        element={
          auth.loading ? <Spinner /> :
          auth.loggedIn && auth.user ? <Navigate to="/dashboard" replace /> :
          <Suspense fallback={<Spinner />}><Login /></Suspense>
        }
      />

      {/* Auth-geschützte App-Seiten (lazy AppShell-Chunk) */}
      <Route
        path="/*"
        element={
          auth.loading ? <Spinner /> :
          !auth.loggedIn || !auth.user ? <Navigate to="/login" replace /> :
          <Suspense fallback={<Spinner />}>
            <AppShell
              accounts={auth.accounts}
              role={auth.role}
              user={auth.user}
              onLogout={() => void auth.logout()}
            />
          </Suspense>
        }
      />
    </Routes>
  );
}
